import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import Select from 'react-select';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import { 
  getDifficultyStyle, 
  getPurposeStyle, 
  getSearchVolumeStyle,
  intentTypes
} from '@/shared/utils/constants';
import {
  DifficultyCellRenderer,
  IntentCellRenderer,
  PurposeCellRenderer,
  SearchVolumeCellRenderer
} from './cellRenderers';
import SearchVolumeModal from './SearchVolumeModal';
import 'react-tooltip/dist/react-tooltip.css';

import { TableView, ListView, CardView, ChartView, TreeView } from './views';
import type { CrawlProgress, KeywordData } from '../types/keyword.types';

function filterRowDataByQuickText(rowData: KeywordData[], quickFilterText: string): KeywordData[] {
  if (!quickFilterText?.trim()) {
    return rowData;
  }

  const filterText = quickFilterText.toLowerCase().trim();
  return rowData.filter((row) => {
    if (row.keyword?.toLowerCase().includes(filterText)) return true;
    if (row.parent_keyword?.toLowerCase().includes(filterText)) return true;
    if (row.source?.toLowerCase().includes(filterText)) return true;
    if (Array.isArray(row.intent) && row.intent.some((item) => item.toLowerCase().includes(filterText))) {
      return true;
    }
    if (Array.isArray(row.purpose) && row.purpose.some((item) => item.toLowerCase().includes(filterText))) {
      return true;
    }
    return false;
  });
}

interface KeywordDataViewProps {
  viewMode?: 'table' | 'list' | 'card' | 'chart' | 'tree';
  quickFilterText?: string;
  onQuickFilterChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  data?: KeywordData[];
  isLoading?: boolean;
  searchProgress?: CrawlProgress | null;
}

interface KeywordDataViewRef {
  exportToCSV: () => void;
  downloadSVG: () => void;
  copyKeywords: () => Promise<void>;
  getRowData: () => KeywordData[];
  toggleFullscreen: () => void;
}

interface GroupByOption {
  value: string;
  label: string;
}

const KeywordDataView = React.forwardRef<KeywordDataViewRef, KeywordDataViewProps>(
  ({ viewMode = 'table', quickFilterText = '', data = [], isLoading = false }, ref) => {
  const { translations, isRTL, language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [openModalId, setOpenModalId] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [groupBy, setGroupBy] = useState<string>('intent'); // 'intent', 'difficulty', 'searchVolume', 'length', 'purpose'
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [tableFullscreen, setTableFullscreen] = useState(false);
  const [treeFullscreen, setTreeFullscreen] = useState(false);
  const chartDownloadRef = useRef<(() => void) | null>(null); // Ref to store downloadSVG function from ChartView
  const treeDownloadRef = useRef<(() => void) | null>(null); // Ref to store downloadSVG function from TreeView

  const rowData = useMemo<KeywordData[]>(() => data, [data]);

  const centerCellStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;

  const baseColumnDefs = useMemo<ColDef[]>(() => [
    {
      field: 'keyword',
      headerName: translations.keyword,
      flex: 2,
      sortable: true,
      filter: true,
      cellClass: 'kw-cell-keyword',
      headerClass: 'kw-col-keyword',
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'difficalty',
      headerName: translations.difficulty,
      flex: 1,
      sortable: true,
      filter: true,
      cellRenderer: DifficultyCellRenderer,
      cellStyle: centerCellStyle,
      headerClass: 'kw-col-center',
      comparator: (valueA, valueB) => {
        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;
        return (valueA as number) - (valueB as number);
      },
    },
    {
      field: 'sv',
      headerName: translations.searchVolume,
      flex: 1,
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: SearchVolumeCellRenderer,
      cellStyle: centerCellStyle,
      headerClass: 'kw-col-center',
      comparator: (valueA, valueB) => {
        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;
        return (valueA as number) - (valueB as number);
      },
    },
    {
      field: 'intent',
      headerName: translations.intent,
      flex: 1.5,
      sortable: true,
      filter: true,
      cellRenderer: IntentCellRenderer,
      cellStyle: centerCellStyle,
      headerClass: 'kw-col-center',
    },
    {
      field: 'purpose',
      headerName: translations.purpose,
      flex: 1.5,
      sortable: true,
      filter: true,
      cellRenderer: PurposeCellRenderer,
      cellStyle: centerCellStyle,
      headerClass: 'kw-col-center',
    },
    {
      field: 'len',
      headerName: translations.length,
      flex: 0.65,
      maxWidth: 96,
      minWidth: 72,
      sortable: true,
      filter: true,
      cellStyle: { textAlign: 'center' },
      headerClass: 'kw-col-center',
    },
  ], [translations]);

  // Column definitions - reverse order for RTL
  const columnDefs = useMemo<ColDef[]>(() => {
    return isRTL ? [...baseColumnDefs].reverse() : baseColumnDefs;
  }, [baseColumnDefs, isRTL]);

  // Export CSV function - defined after columnDefs
  const exportToCSV = useCallback(() => {
    let allRowData: KeywordData[] = [];
    let groupedData: Record<string, KeywordData[]> | null = null;

    // For table view, get data from grid API
    if (viewMode === 'table' && gridRef.current && gridRef.current.api) {
      gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
        allRowData.push(node.data);
      });
    } else {
      // For list/card view, use rowData directly and apply quickFilterText filter
      let filteredData = rowData;
      
      if (quickFilterText && quickFilterText.trim() !== '') {
        const filterText = quickFilterText.toLowerCase().trim();
        filteredData = rowData.filter(row => {
          // Search in keyword
          if (row.keyword && row.keyword.toLowerCase().includes(filterText)) {
            return true;
          }
          // Search in parent_keyword
          if (row.parent_keyword && row.parent_keyword.toLowerCase().includes(filterText)) {
            return true;
          }
          // Search in source
          if (row.source && row.source.toLowerCase().includes(filterText)) {
            return true;
          }
          // Search in intent array
          if (Array.isArray(row.intent) && row.intent.some(i => i.toLowerCase().includes(filterText))) {
            return true;
          }
          // Search in purpose array
          if (Array.isArray(row.purpose) && row.purpose.some(p => p.toLowerCase().includes(filterText))) {
            return true;
          }
          return false;
        });
      }
      
      // Group data based on groupBy for list/card view
      const groups: Record<string, KeywordData[]> = {};
      
      filteredData.forEach(item => {
        let groupKey = 'other';
        
        if (groupBy === 'intent') {
          if (item.intent && Array.isArray(item.intent) && item.intent.length > 0) {
            groupKey = item.intent[0];
          } else {
            groupKey = 'no-intent';
          }
        } else if (groupBy === 'difficulty') {
          if (item.difficalty !== null && item.difficalty !== undefined) {
            const style = getDifficultyStyle(item.difficalty);
            groupKey = style ? style.label : 'unknown';
          } else {
            groupKey = 'no-difficulty';
          }
        } else if (groupBy === 'searchVolume') {
          if (item.sv !== null && item.sv !== undefined) {
            if (item.sv < 1000) {
              groupKey = language === 'fa' ? 'کم (زیر 1000)' : 'Low (< 1K)';
            } else if (item.sv < 10000) {
              groupKey = language === 'fa' ? 'متوسط (1K-10K)' : 'Medium (1K-10K)';
            } else if (item.sv < 100000) {
              groupKey = language === 'fa' ? 'زیاد (10K-100K)' : 'High (10K-100K)';
            } else {
              groupKey = language === 'fa' ? 'خیلی زیاد (بالای 100K)' : 'Very High (> 100K)';
            }
          } else {
            groupKey = 'no-volume';
          }
        } else if (groupBy === 'length') {
          groupKey = `${item.len || 0} ${language === 'fa' ? 'کلمه' : 'words'}`;
        } else if (groupBy === 'purpose') {
          if (item.purpose && Array.isArray(item.purpose) && item.purpose.length > 0) {
            const purposeConfig = getPurposeStyle(item.purpose[0]);
            groupKey = purposeConfig ? (language === 'fa' ? purposeConfig.label : purposeConfig.enLabel) : item.purpose[0];
          } else {
            groupKey = 'no-purpose';
          }
        } else if (groupBy === 'source') {
          groupKey = item.source || 'no-source';
        }
        
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
      });
      
      groupedData = groups;
    }

    // Use columnDefs for table view, or create headers manually for list/card view
    let headers: string[] = [];
    if (viewMode === 'table' && columnDefs && columnDefs.length > 0) {
      headers = columnDefs.map(colDef => {
        return colDef.headerName || colDef.field || '';
      });
    } else {
      // For list/card view, use standard headers
      headers = [
        translations.keyword || 'Keyword',
        translations.difficulty || 'Difficulty',
        translations.searchVolume || 'Search Volume',
        translations.intent || 'Intent',
        translations.purpose || 'Purpose',
        translations.length || 'Length',
        // translations.parentKeyword || 'Parent Keyword',
        translations.source || 'Source'
      ];
    }

    // Create CSV rows
    const csvRows = [headers.join(',')];

    // Helper function to format and escape CSV values
    const formatValue = (val: any): string => {
      if (val === null || val === undefined) return '';
      if (Array.isArray(val)) return val.join('; ');
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    };

    const escapeCSV = (val: any): string => {
      const str = formatValue(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Get group label helper
    const getGroupLabel = (groupKey: string): string => {
      if (groupBy === 'intent') {
        const intent = intentTypes[groupKey];
        if (intent) {
          return language === 'fa' ? intent.label : intent.enLabel;
        }
        return groupKey === 'no-intent' ? (language === 'fa' ? 'بدون نیت' : 'No Intent') : groupKey;
      } else if (groupBy === 'difficulty') {
        return groupKey === 'no-difficulty' ? (language === 'fa' ? 'بدون سختی' : 'No Difficulty') : groupKey;
      } else if (groupBy === 'searchVolume') {
        return groupKey === 'no-volume' ? (language === 'fa' ? 'بدون حجم جستجو' : 'No Search Volume') : groupKey;
      } else if (groupBy === 'length') {
        return groupKey;
      } else if (groupBy === 'purpose') {
        return groupKey === 'no-purpose' ? (language === 'fa' ? 'بدون هدف' : 'No Purpose') : groupKey;
      } else if (groupBy === 'source') {
        return groupKey === 'no-source' ? (language === 'fa' ? 'بدون منبع' : 'No Source') : groupKey;
      }
      return groupKey;
    };

    if (viewMode === 'table') {
      // For table view, just add all rows
      allRowData.forEach(row => {
        let values: string[] = [];
        
        if (columnDefs && columnDefs.length > 0) {
          values = columnDefs.map(colDef => {
            const field = colDef.field;
            if (!field) return '';
            
            let value = row[field];
            return escapeCSV(value);
          });
        }
        
        csvRows.push(values.join(','));
      });
    } else if (groupedData) {
      // For list/card view, add grouped data
      const groupKeys = Object.keys(groupedData).sort();
      
      groupKeys.forEach(groupKey => {
        const groupItems = groupedData![groupKey];
        const groupLabel = getGroupLabel(groupKey);
        
        // Add group header
        csvRows.push('');
        csvRows.push(`${escapeCSV(groupLabel)} (${groupItems.length} ${language === 'fa' ? 'مورد' : 'items'})`);
        csvRows.push('');
        
        // Add items in this group
        groupItems.forEach(row => {
          const values = [
            escapeCSV(row.keyword),
            escapeCSV(row.difficalty),
            escapeCSV(row.sv),
            escapeCSV(row.intent),
            escapeCSV(row.purpose),
            escapeCSV(row.len),
            // escapeCSV(row.parent_keyword),
            escapeCSV(row.source)
          ];
          csvRows.push(values.join(','));
        });
      });
    }

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `keywords_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [columnDefs, viewMode, rowData, quickFilterText, translations, groupBy, language]);

  const copyKeywords = useCallback(async () => {
    let rows: KeywordData[] = [];

    if (viewMode === 'table' && gridRef.current?.api) {
      gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) {
          rows.push(node.data);
        }
      });
    } else {
      rows = filterRowDataByQuickText(rowData, quickFilterText);
    }

    const text = rows.map((row) => row.keyword).join('\n');
    await navigator.clipboard.writeText(text);
  }, [viewMode, rowData, quickFilterText]);

  // Download SVG function for chart - calls ChartView's download function
  const downloadChartSVG = useCallback(() => {
    if (viewMode === 'chart' && chartDownloadRef.current) {
      chartDownloadRef.current();
    } else if (viewMode === 'tree' && treeDownloadRef.current) {
      treeDownloadRef.current();
    }
  }, [viewMode]);

  // Expose export function to parent via ref
  React.useImperativeHandle(ref, () => ({
    exportToCSV: exportToCSV,
    downloadSVG: downloadChartSVG,
    copyKeywords,
    getRowData: () => rowData,
    toggleFullscreen: () => {
      if (viewMode === 'chart') {
        setChartFullscreen(prev => !prev);
      } else if (viewMode === 'tree') {
        setTreeFullscreen(prev => !prev);
      } else {
        // For table/list/card, we'll implement fullscreen modal
        // For now, just toggle a state that can be used for fullscreen modal
        setTableFullscreen(prev => !prev);
      }
    }
  }), [exportToCSV, downloadChartSVG, copyKeywords, viewMode, rowData]);

  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    headerClass: isRTL ? 'ag-header-cell-rtl' : 'ag-header-cell-ltr',
  }), [isRTL]);

  // Group By options for react-select
  const groupByOptions = useMemo<GroupByOption[]>(() => [
    { value: 'intent', label: language === 'fa' ? 'نیت' : 'Intent' },
    { value: 'difficulty', label: language === 'fa' ? 'سختی' : 'Difficulty' },
    { value: 'searchVolume', label: language === 'fa' ? 'حجم جستجو' : 'Search Volume' },
    { value: 'length', label: language === 'fa' ? 'طول کلمه' : 'Length' },
    { value: 'purpose', label: language === 'fa' ? 'هدف' : 'Purpose' },
    { value: 'source', label: language === 'fa' ? 'منبع' : 'Source' },
  ], [language]);

  // Custom styles for react-select
  const customStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      borderColor: isDark ? '#4b5563' : '#d1d5db',
      color: isDark ? '#f3f4f6' : '#111827',
      minHeight: '42px',
      '&:hover': {
        borderColor: '#6e46e5',
      },
      boxShadow: state.isFocused ? '0 0 0 2px rgba(110, 70, 229, 0.2)' : 'none',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      maxHeight: '300px',
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: '300px',
      padding: '4px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#6e46e5'
        : state.isFocused
        ? isDark ? '#374151' : '#f3f4f6'
        : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : isDark ? '#f3f4f6' : '#111827',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#6e46e5',
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
    }),
    input: (base: any) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? '#9ca3af' : '#6b7280',
    }),
  }), [isDark]);

  // Handle escape key to exit fullscreen for table/list/card
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && tableFullscreen) {
        setTableFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [tableFullscreen]);

  // Render content based on view mode
  const renderContentView = (isFullscreen = false) => {
    if (!isLoading && rowData.length === 0) {
      return (
        <div className="py-16 text-center text-gray-500 dark:text-gray-400">
          {language === 'fa'
            ? 'کلمه کلیدی را وارد کنید و جستجو را بزنید.'
            : 'Enter a keyword and start searching.'}
        </div>
      );
    }

    return (
      <>
        {isLoading && (
          <div className="mb-4 flex items-center gap-3 rounded-lg bg-purple-50 dark:bg-gray-800 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <TableView
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            gridRef={gridRef}
            quickFilterText={quickFilterText}
            isRTL={isRTL}
            isDark={isDark}
            language={language}
            openModalId={openModalId}
            setOpenModalId={setOpenModalId}
            isFullscreen={isFullscreen}
          />
        )}

      {/* List View - Grouped cards */}
      {viewMode === 'list' && (
        <ListView
          data={rowData}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          quickFilterText={quickFilterText}
          language={language}
          isDark={isDark}
          isRTL={isRTL}
          translations={translations}
        />
      )}

      {/* Chart View - Radial chart */}
      {viewMode === 'chart' && (
        <div className="w-full">
          {/* Group By Selector */}
          <div className={`mb-4 flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
            {!isRTL && (
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
              </label>
            )}
            <div style={{ minWidth: '200px' }} dir={isRTL ? 'rtl' : 'ltr'}>
              <Select<GroupByOption>
                value={groupByOptions.find(option => option.value === groupBy)}
                onChange={(selectedOption) => {
                  setGroupBy(selectedOption?.value || 'intent');
                }}
                options={groupByOptions}
                isSearchable={false}
                isClearable={false}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                theme={(selectTheme) => ({
                  ...selectTheme,
                  colors: {
                    ...selectTheme.colors,
                    primary: '#6e46e5',
                    primary75: '#8b6ce8',
                    primary50: '#a894eb',
                    primary25: '#c5b8ee',
                  },
                })}
              />
            </div>
            {isRTL && (
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
              </label>
            )}
          </div>

          {/* Chart View */}
          <ChartView 
            data={rowData}
            groupBy={groupBy}
            quickFilterText={quickFilterText}
            language={language}
            isDark={isDark}
            isRTL={isRTL}
            translations={translations}
            isFullscreen={chartFullscreen}
            onToggleFullscreen={() => setChartFullscreen(prev => !prev)}
            onDownloadSVGRef={(downloadFn) => { chartDownloadRef.current = downloadFn; }}
          />
        </div>
      )}

      {/* Card View - Grid layout with cards side by side */}
      {viewMode === 'card' && (
        <div className="w-full">
          {/* Group By Selector */}
          <div className={`mb-4 flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
            {!isRTL && (
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
              </label>
            )}
            <div style={{ minWidth: '200px' }} dir={isRTL ? 'rtl' : 'ltr'}>
              <Select<GroupByOption>
                value={groupByOptions.find(option => option.value === groupBy)}
                onChange={(selectedOption) => {
                  setGroupBy(selectedOption?.value || 'intent');
                }}
                options={groupByOptions}
                isSearchable={false}
                isClearable={false}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                theme={(selectTheme) => ({
                  ...selectTheme,
                  colors: {
                    ...selectTheme.colors,
                    primary: '#6e46e5',
                    primary75: '#8b6ce8',
                    primary50: '#a894eb',
                    primary25: '#c5b8ee',
                  },
                })}
              />
            </div>
            {isRTL && (
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
              </label>
            )}
          </div>

          {/* Card Grid */}
          <CardView 
            data={rowData}
            groupBy={groupBy}
            quickFilterText={quickFilterText}
            language={language}
            isDark={isDark}
            isRTL={isRTL}
            translations={translations}
            viewType="card"
          />
        </div>
      )}

      {/* Tree View - Hierarchical tree visualization */}
      {viewMode === 'tree' && (
        <div className="w-full">
          {/* Group By Selector */}
          <div className={`mb-4 flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
            {!isRTL && (
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
              </label>
            )}
            <div style={{ minWidth: '200px' }} dir={isRTL ? 'rtl' : 'ltr'}>
              <Select<GroupByOption>
                value={groupByOptions.find(option => option.value === groupBy)}
                onChange={(selectedOption) => {
                  setGroupBy(selectedOption?.value || 'intent');
                }}
                options={groupByOptions}
                isSearchable={false}
                isClearable={false}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                theme={(selectTheme) => ({
                  ...selectTheme,
                  colors: {
                    ...selectTheme.colors,
                    primary: '#6e46e5',
                    primary75: '#8b6ce8',
                    primary50: '#a894eb',
                    primary25: '#c5b8ee',
                  },
                })}
              />
            </div>
            {isRTL && (
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
              </label>
            )}
          </div>

          {/* Tree View */}
          <TreeView 
            data={rowData}
            groupBy={groupBy}
            quickFilterText={quickFilterText}
            language={language}
            isDark={isDark}
            isRTL={isRTL}
            translations={translations}
            isFullscreen={treeFullscreen}
            onToggleFullscreen={() => setTreeFullscreen(prev => !prev)}
            onDownloadSVGRef={(downloadFn) => { treeDownloadRef.current = downloadFn; }}
          />
        </div>
      )}
      {/* Global Tooltip Component - renders tooltip for all difficulty cells */}
      <Tooltip 
        id="difficulty-tooltip-global"
        place="top"
        className="custom-difficulty-tooltip"
        clickable={false}
        offset={10}
        delayShow={300}
      />
      {/* Global Tooltip Component - renders tooltip for all intent cells */}
      <Tooltip 
        id="intent-tooltip-global"
        place="top"
        className="custom-difficulty-tooltip"
        clickable={false}
        offset={10}
        delayShow={300}
      />
      {/* Global Tooltip Component - renders tooltip for all purpose cells */}
      <Tooltip 
        id="purpose-tooltip-global"
        place="top"
        className="custom-difficulty-tooltip"
        clickable={false}
        offset={10}
        delayShow={300}
      />
      {/* Global Tooltip Component - renders tooltip for search volume cells */}
      <Tooltip 
        id="search-volume-tooltip-global"
        place="top"
        className="custom-difficulty-tooltip"
        clickable={false}
        offset={10}
        delayShow={300}
      />
      <Tooltip
        id="coming-soon-tooltip-global"
        place="top"
        positionStrategy="fixed"
        className="!bg-gray-800 !text-white !text-sm !px-3 !py-2 !rounded-lg !shadow-lg kw-app-tooltip"
        style={{ zIndex: 999999 }}
        delayShow={200}
      />
      
        {/* Search Volume Modal - renders outside grid */}
        {openModalId && (() => {
          // Extract row ID from modalId (format: "sv-modal-{rowId}")
          const rowId = openModalId.replace('sv-modal-', '');
          
          // Find the row data for the open modal
          // Since getRowId returns keyword, rowId should be the keyword
          const modalRow = rowData.find(row => row?.keyword === rowId);
          
          if (!modalRow || !modalRow.sv || modalRow.sv === null) return null;
          
          const style = getSearchVolumeStyle(modalRow.sv);
          
          return (
            <SearchVolumeModal
              key={openModalId}
              isOpen={true}
              onClose={() => setOpenModalId(null)}
              keyword={modalRow.keyword || ''}
              searchVolume={modalRow.sv}
              trendData={modalRow.sv_trend || []}
              color={style.color}
            />
          );
        })()}
      </>
    );
  };

  return (
    <>
      {/* Fullscreen Modal for Table/List/Card */}
      {tableFullscreen && (
        <div
          className="fixed inset-0 z-[999999] bg-white dark:bg-gray-900 flex flex-col items-center justify-center p-8"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Close Button */}
          <button
            onClick={() => setTableFullscreen(false)}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 rounded-md transition-colors ${
              isDark
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-tooltip-id="action-tooltip"
            data-tooltip-content={language === 'fa' ? 'بستن' : 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Fullscreen Content */}
          <div className="w-full h-full flex flex-col items-center justify-center overflow-auto">
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-[95vw] max-h-[95vh] overflow-auto`}>
              {renderContentView(true)}
            </div>
          </div>
        </div>
      )}

      {/* Normal View */}
      {!tableFullscreen && (
        <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6`} style={{ position: 'relative', zIndex: 1 }}>
          {renderContentView()}
        </div>
      )}
    </>
  );
});

KeywordDataView.displayName = 'KeywordDataView';

export default KeywordDataView;

